<template>
  <b-card v-if="attachments.length" class="card-box w-100 mb-4 mx-auto card__download-all-file">
    <div class="row__download-all-file">
      <p class="font-weight-bold m-0">Total Files submitted: {{ attachments.length }}</p>

      <div>
        <b-button
          variant="outline-primary"
          class="button__download-all"
          :disabled="isDownloading"
          @click="downloadAll"
        >
          <span v-if="!isDownloading"> Download All </span>
          <span v-else> Downloading... </span>
          <font-awesome-icon v-if="!isDownloading" :icon="['fas', 'arrow-down']" />
        </b-button>
      </div>
    </div>
    <div v-if="isDownloading" class="wrapper__progress-bar mt-2">
      <div class="progress">
        <div
          class="progress-bar"
          role="progressbar"
          :style="{ width: downloadProgress + '%' }"
          :aria-valuenow="downloadProgress"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
      <div>{{ downloadProgress }}%</div>
    </div>
  </b-card>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import JSZip from 'jszip';
import PostResponse from '@/models/PostResponse';

library.add(faArrowDown);

export default {
  components: {
    FontAwesomeIcon
  },
  props: {
    evidenceSubmission: {
      type: Object,
      default: null
    },
    learningUnitName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isDownloading: false,
      downloadProgress: 0,
      animationId: null,
      targetProgress: 0
    };
  },
  computed: {
    postResponses() {
      if (!this.evidenceSubmission?.post_response_ids) {
        return [];
      }
      return PostResponse.query()
        .where('id', this.evidenceSubmission.post_response_ids)
        .where('assessment_submission', (value) => value !== null)
        .get();
    },

    attachments() {
      return this.postResponses.flatMap((postResponse) => postResponse.attachments || []);
    }
  },

  beforeDestroy() {
    if (this.animationId) cancelAnimationFrame(this.animationId);
  },

  methods: {
    /**
     * Generate unique filename to avoid duplicates in zip archive
     * @param {string} name - Original filename
     * @param {object} counts - Object tracking filename occurrence count
     * @returns {string} Unique filename with numeric suffix if duplicate (e.g., photo, photo_1, photo_2)
     */
    getUniqueName(name, counts) {
      if (!counts[name]) {
        counts[name] = 1;
        return name;
      }

      const dot = name.lastIndexOf('.');
      const base = dot > 0 ? name.slice(0, dot) : name;
      const ext = dot > 0 ? name.slice(dot) : '';

      return `${base}_${counts[name]++}${ext}`;
    },

    /**
     * Download a single file and track byte-level progress.
     * @param {string} url - File URL to fetch
     * @param {number} totalBytes - Total bytes across all files (for progress calculation)
     * @param {{ received: number }} bytesTracker - Shared tracker object mutated in place
     * @returns {Promise<Blob>}
     */
    async fetchWithProgress(url, totalBytes, bytesTracker) {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw response;

      // If we know the total, stream and count bytes; otherwise fall back to blob()
      if (totalBytes > 0 && response.body) {
        const reader = response.body.getReader();
        const chunks = [];

        let done = false;
        while (!done) {
          const { done: chunkDone, value } = await reader.read();
          done = chunkDone;
          if (done) break;
          chunks.push(value);
          bytesTracker.received += value.byteLength;
          // Map received bytes to 0–80% of progress
          this.downloadProgress = Math.min(
            80,
            Math.round((bytesTracker.received / totalBytes) * 80)
          );
        }

        return new Blob(chunks);
      }

      return response.blob();
    },

    /**
     * Smoothly animates downloadProgress toward a target value.
     *
     * @param {number} value - Target progress percentage (0–100)
     */
    animateProgress(value) {
      this.targetProgress = value;
      if (this.animationId) return;

      const tick = () => {
        this.downloadProgress = Math.min(this.downloadProgress + 1, this.targetProgress);

        if (this.downloadProgress < this.targetProgress) {
          this.animationId = requestAnimationFrame(tick);
        } else {
          this.animationId = null;
        }
      };

      this.animationId = requestAnimationFrame(tick);
    },

    /**
     * Download all attached files as a single zip
     */
    async downloadAll() {
      this.isDownloading = true;
      this.downloadProgress = 0;

      try {
        const zip = new JSZip();
        const counts = {};

        // Pre-fetch Content-Length for all files so we can show accurate progress
        let totalBytes = 0;
        const contentLengths = await Promise.all(
          this.attachments.map(async (file) => {
            if (!file.url) return 0;
            try {
              const head = await fetch(file.url, { method: 'HEAD', mode: 'cors', credentials: 'omit' });
              return parseInt(head.headers.get('Content-Length') || '0', 10) || 0;
            } catch {
              return 0;
            }
          })
        );
        totalBytes = contentLengths.reduce((sum, n) => sum + n, 0);

        const bytesTracker = { received: 0 };

        for (let i = 0; i < this.attachments.length; i++) {
          const file = this.attachments[i];

          if (!file.url) throw new Error(`No download URL: ${file.name}`);

          const blob = await this.fetchWithProgress(file.url, totalBytes, bytesTracker);

          let name = file.name;

          // Ensure unique file names in the zip and handle duplicates
          name = this.getUniqueName(name, counts);

          // add file to zip
          zip.file(name, blob);

          // If Content-Length was unavailable, fall back to per-file step progress
          if (totalBytes === 0) {
            this.downloadProgress = Math.round(((i + 1) / this.attachments.length) * 80);
          }
        }

        // Generate the zip file and update progress from 80% → 100% based on metadata from JSZip
        const content = await zip.generateAsync({ type: 'blob' }, (metadata) => {
          // Update progress zipping (80% to 100%)
          this.animateProgress(80 + Math.round(metadata.percent * 0.2));
        });

        // ensure progress is at 100% before triggering download
        this.animateProgress(100);

        const url = URL.createObjectURL(content);
        Object.assign(document.createElement('a'), {
          href: url,
          download:
            this.learningUnitName && this.evidenceSubmission.code
              ? `${this.learningUnitName}-${this.evidenceSubmission.code}-attachments.zip`
              : 'evidence_submission_files.zip'
        }).click();

        URL.revokeObjectURL(url);
      } catch (err) {
        console.error(err);
        this.$toast.error('Failed to create zip, please try again later.');
      } finally {
        this.isDownloading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.card__download-all-file {
  max-width: 910px;
  margin: auto;
}

.row__download-all-file {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

// custom styles for the download all button
.button__download-all {
  border-radius: 6px;
  min-height: 38px;

  svg {
    margin-left: 8px;
    border-bottom: 3px solid #3c44b1;
    transition: border-bottom 0.3s ease;
  }

  &:hover svg {
    border-bottom: 3px solid #ffffff;
  }
}

.wrapper__progress-bar {
  display: flex;
  align-items: center;
  gap: 12px;

  .progress {
    width: 100%;
    height: 12px;

    .progress-bar {
      transition: width 0.3s ease;
      animation: pulse 1s ease-in-out infinite alternate;
    }

    @keyframes pulse {
      from {
        opacity: 1;
      }
      to {
        opacity: 0.6;
      }
    }
  }
}
</style>
